import React, { useEffect, useState } from 'react';
import { Card, Drawer, message, Select, Spin, Tabs, notification, Typography, Button } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import UIStore from '../../stores/UIStore';
import { NewOrderForm } from '../../components/SalesOrders';
import SalesOrderStore from '../../stores/SalesOrderStore';
import api from '../../utils/api';
import { QuotedLineItems } from '../../components/SalesOrders/QuotedLineItems';
import ApplicationCostItems from '../../components/SalesOrders/ApplicationCostItems';
import { AdditionalRequirementsProps } from '../../types/types';
import _ from 'lodash';
import QuoteStore from '../../stores/QuoteStore';
import { QuoteProps } from '@partsbadger/types';
import ContactStore from '../../stores/ContactStore';
import { observer } from 'mobx-react';
import { ErrorRender } from '@partsbadger/library';
import { PageHeader } from '../../components/PageHeader';
import { ArrowLeftOutlined } from '@ant-design/icons';
import Feedback from '../../components/Feedback';

const { Option } = Select;
const { Title } = Typography;

const { TabPane } = Tabs;

interface IProps {
    match: {
        params: {
            id: number;
        };
    };
}

const SalesOrderCreateForm = ({ match }: IProps) => {
    const history = useHistory();

    const [loading, setLoading] = useState<boolean>(false);
    const [showOriginalItems, setShowOriginalItems] = useState<boolean>(false);
    const [parentLineItems, setParentLineItems] = useState([]);
    const [additional_requirements, setAdditionalRequirements] = useState<Array<AdditionalRequirementsProps>>([]);
    const [quotes, setQuotes] = useState<Array<QuoteProps>>([]);
    const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);

    const { id } = match.params;

    useEffect(() => {
        UIStore.setSelectedKey(['quotes']);
        UIStore.setDocumentTitle('Order New');
        ContactStore.getAdditionalRequirements();
    }, []);

    useEffect(() => {
        setLoading(true);
        SalesOrderStore.getOrderById(id).then(() => {
            setLoading(false);
        });
    }, [id]);

    useEffect(() => {
        async function getAddressAndDocuments() {
            if (SalesOrderStore.order?.contact.id) {
                try {
                    await ContactStore.getContactById(SalesOrderStore.order?.contact.id);
                    await ContactStore.getAdditionalRequirementsAndDocuments(SalesOrderStore.order?.contact.id);
                } catch (err: any) {
                    notification.error({
                        duration: 10,
                        message: 'Error',
                        description: <ErrorRender error={err} />,
                    });
                }
            }
        }

        getAddressAndDocuments();
    }, [SalesOrderStore.order?.contact.id]);

    if (ContactStore.loading || !ContactStore.contact) {
        return (
            <div className="w-full h-full flex  justify-center">
                <Spin spinning tip={'Loading..'} />
            </div>
        );
    }

    const getAdditionalRequirements = async (id: number) => {
        const response = await api.Quotes.getAdditionalRequirements(id);
        setAdditionalRequirements(response.results);
    };

    const loadParentLineItems = async (quote_id: number) => {
        await api.Quotes.getLineItems(quote_id).then(data => {
            setParentLineItems(data.results);
        });
    };

    const handleSearch = _.debounce(async (value: string) => {
        if (value) {
            QuoteStore.searchQuotes(value).then(response => {
                setQuotes(response.results);
            });
        } else {
            setQuotes([]);
        }
    }, 500);

    const redirectToOrders = () => {
        history.push('/my-account/orders');
    };

    if (SalesOrderStore.order?.id && SalesOrderStore.order?.status !== 'Draft') {
        redirectToOrders();
    }

    const order_items = SalesOrderStore.order_items ?? [];
    const isADraftQuote =
        order_items.filter(item => item.quote_has_product?.quote_related?.stage === 'Draft').length > 0;

    const quoteId = order_items.find(item => item.quote_has_product?.quote_related?.stage === 'Draft')
        ?.quote_has_product?.quote_related?.id;

    return (
        <>
            <PageHeader>
                <div className="d-flex justify-start align-items-baseline">
                    <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            if (isADraftQuote) {
                                history.push(`/my-account/quotes/${quoteId}/continue/`);
                            } else {
                                history.goBack();
                            }
                        }}
                        className="text-primary m-0 p-0"
                    />
                    <Title level={1} className="mb-0">
                        Checkout
                    </Title>
                </div>
            </PageHeader>
            <div className="container">
                <div>
                    {!SalesOrderStore.order?.id || loading ? (
                        <div className="w-full h-full flex  justify-center">
                            <Spin spinning tip={'Loading..'} />
                            {showFeedbackModal && (
                                <Feedback
                                    isQuotes={true}
                                    visible={showFeedbackModal}
                                    setVisible={setShowFeedbackModal}
                                    redirectToThisPage={() => redirectToOrders()}
                                />
                            )}
                        </div>
                    ) : (
                        <NewOrderForm
                            order={SalesOrderStore.order}
                            contact={ContactStore.contact}
                            order_items={SalesOrderStore.order_items}
                            onSubmitted={data => {
                                message.success('Thank you for your order!');

                                if (data?.showQuoteSurvey) {
                                    setShowFeedbackModal(true);
                                } else {
                                    redirectToOrders();
                                }
                            }}
                        />
                    )}
                </div>

                <Drawer
                    closable
                    title="Quoted Parts and Additional Requirements"
                    placement="left"
                    onClose={() => setShowOriginalItems(false)}
                    open={showOriginalItems}
                    width={800}
                >
                    <Select
                        showSearch
                        placeholder="Search a quote..."
                        style={{ width: '100%' }}
                        defaultActiveFirstOption={false}
                        showArrow={false}
                        filterOption={false}
                        onSearch={handleSearch}
                        onSelect={(id: number) => {
                            loadParentLineItems(id);
                            getAdditionalRequirements(id);
                        }}
                        notFoundContent={null}
                    >
                        {quotes.map((quote: QuoteProps) => {
                            return (
                                <Option key={quote.id} value={quote.id}>
                                    {quote.name}
                                </Option>
                            );
                        })}
                    </Select>

                    <Tabs defaultActiveKey="1">
                        <TabPane tab={`Parts (${parentLineItems.length})`} key="1">
                            <QuotedLineItems
                                parentLineItems={parentLineItems}
                                handleAddItem={async order_item => {
                                    await SalesOrderStore.addOrderItemDraft(id, order_item);
                                }}
                            />
                        </TabPane>
                        <TabPane tab={`Additional Requirements (${additional_requirements.length})`} key="2">
                            <ApplicationCostItems
                                handleAdd={async item => {
                                    await SalesOrderStore.addOrderItemDraft(id, {
                                        name: 'CNC Part',
                                        product_description: item.additional_requirement,
                                        quantity: 1,
                                        unit_price: parseFloat(item.price),
                                    });
                                }}
                                application_cost={additional_requirements}
                            />
                        </TabPane>
                    </Tabs>
                </Drawer>
            </div>
        </>
    );
};

export default observer(SalesOrderCreateForm);
